import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ShieldCheck,
  HelpCircle,
  Trash2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useAuthStatus } from "../lib/useAuthStatus";
import { listRoutines, deleteRoutine } from "../lib/api";
import SeoHead from "../components/SeoHead";

const VERDICT_VISUAL = {
  // Current verdict enum (matches the single-check + routine payload).
  SAFE_TOGETHER: {
    chip: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Icon: ShieldCheck,
    label: "No label conflict found",
  },
  USE_WITH_CAUTION: {
    chip: "bg-amber-100 text-amber-900 border-amber-200",
    Icon: AlertTriangle,
    label: "Use with caution",
  },
  AVOID: {
    chip: "bg-rose-100 text-rose-800 border-rose-200",
    Icon: AlertCircle,
    label: "Avoid",
  },
  INSUFFICIENT_DATA: {
    chip: "bg-cream-100 text-ink-soft border-surface-line",
    Icon: HelpCircle,
    label: "Not yet reviewed",
  },
  // Legacy keys kept so routines saved before the enum change still render.
  // Aligned to the current accurate wording — never a reassuring overstatement.
  SAFE: {
    chip: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Icon: ShieldCheck,
    label: "No label conflict found",
  },
  MOSTLY_SAFE: {
    chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Icon: CheckCircle2,
    label: "No label conflict found",
  },
  CAUTION: {
    chip: "bg-amber-100 text-amber-900 border-amber-200",
    Icon: AlertTriangle,
    label: "Use with caution",
  },
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function EmptyState() {
  return (
    <div
      data-testid="my-routines-empty"
      className="rounded-2xl border border-surface-line bg-white p-10 sm:p-14 text-center space-y-5"
    >
      <Sparkles className="w-8 h-8 text-ink-mute mx-auto" />
      <h2 className="text-xl sm:text-2xl font-extrabold tracking-[-0.01em] text-ink">
        No saved routines yet
      </h2>
      <p className="text-ink-soft max-w-md mx-auto text-[15px]">
        Run a routine check, then tap "Save routine" on the result to revisit
        it any time.
      </p>
      <Link
        to="/routine"
        data-testid="my-routines-cta-empty"
        className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
      >
        Build your first routine <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

function RoutineCard({ routine, onDelete }) {
  const v = VERDICT_VISUAL[routine.verdict] || VERDICT_VISUAL.INSUFFICIENT_DATA;
  const Icon = v.Icon;
  const itemCount = routine.items?.length || 0;
  const medCount = routine.medication_names?.length || 0;
  return (
    <article
      data-testid={`my-routine-${routine.id}`}
      className="rounded-2xl bg-white border border-surface-line p-5 sm:p-6 flex flex-col gap-4 hover:border-ink/20 hover:shadow-sm transition-all"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-bold text-ink truncate">
            {routine.label || "Untitled routine"}
          </h3>
          <div className="text-[12px] text-ink-mute flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-1">
            <Calendar className="w-3 h-3" />
            <span>{relTime(routine.created_at)}</span>
            <span aria-hidden="true">·</span>
            <span>{itemCount} {itemCount === 1 ? "product" : "products"}</span>
            {medCount > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <span>{medCount} {medCount === 1 ? "med" : "meds"}</span>
              </>
            )}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-3 h-7 mt-0.5 rounded-full border font-bold text-[11px] uppercase tracking-wide shrink-0 ${v.chip}`}
        >
          <Icon className="w-3 h-3" /> {v.label}
        </span>
      </div>

      {routine.medication_names?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {routine.medication_names.slice(0, 3).map((m) => (
            <span
              key={m}
              className="inline-flex items-center px-2 h-6 rounded-full bg-cream-100 border border-surface-line text-[11px] font-semibold text-ink"
            >
              {m}
            </span>
          ))}
          {routine.medication_names.length > 3 && (
            <span className="inline-flex items-center px-2 h-6 rounded-full text-[11px] text-ink-mute">
              +{routine.medication_names.length - 3} more
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Link
          to={`/routine?reopen=${encodeURIComponent(routine.id)}`}
          data-testid={`my-routine-reopen-${routine.id}`}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 font-bold text-sm transition-all flex-1 justify-center"
        >
          Reopen <ArrowRight className="w-4 h-4" />
        </Link>
        <button
          type="button"
          onClick={onDelete}
          data-testid={`my-routine-delete-${routine.id}`}
          aria-label="Delete saved routine"
          className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-surface-line text-ink-mute hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </article>
  );
}

export default function MyRoutines() {
  const { user, ready } = useAuth();
  const isAuthed = !!user;
  const { authEnabled } = useAuthStatus();
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ready) return;
    if (!isAuthed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listRoutines()
      .then((r) => {
        if (cancelled) return;
        setRoutines(r.items || []);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Couldn't load your routines", {
          description: "Check your connection and refresh \u2014 your saved routines are safe.",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthed, ready]);

  const handleDelete = async (id) => {
    const prior = routines;
    setRoutines((rs) => rs.filter((r) => r.id !== id));
    try {
      await deleteRoutine(id);
      toast.success("Routine deleted", {
        description: "Removed from your saved routines \u2014 you can build a new one any time.",
      });
    } catch {
      setRoutines(prior);
      toast.error("Couldn't delete that routine", {
        description: "Check your connection and try again \u2014 your routine is still safe.",
      });
    }
  };

  if (!isAuthed) {
    return (
      <main
        className="bg-cream-50 min-h-[80vh] flex items-center justify-center px-5"
        data-testid="my-routines-signed-out"
      >
        <SeoHead
          title="My routines — Blendsafe"
          description="Sign in to save your skincare routines and revisit them any time."
          canonicalPath="/routines"
        />
        <div className="max-w-md text-center space-y-5">
          <h1 className="text-3xl font-extrabold tracking-[-0.02em] text-ink">
            Sign in to see your routines
          </h1>
          <p className="text-ink-soft">
            {authEnabled === false
              ? "Sign-in is launching very soon — your saved routines and watchlist will appear here."
              : "Sign in with your email to save routines and revisit them any time."}
          </p>
          <button
            type="button"
            data-testid="my-routines-signin-btn"
            onClick={() => navigate("/?auth=1")}
            disabled={authEnabled === false}
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {authEnabled === false ? "Sign-in unavailable" : "Sign in"}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="my-routines-page">
      <SeoHead
        title="My routines — Blendsafe"
        description="Your saved skincare routines, in one place."
        canonicalPath="/routines"
      />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-7">
        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-[-0.02em] text-ink">
              Your saved routines
            </h1>
            <p className="text-ink-soft mt-2 max-w-xl">
              Every routine you save lives here. Tap one to reopen it and
              re-check against your medications.
            </p>
          </div>
          {routines.length > 0 && (
            <Link
              to="/routine"
              data-testid="my-routines-cta-new"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              Build a new routine <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </header>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-44 rounded-2xl bg-cream-200" />
            ))}
          </div>
        ) : routines.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {routines.map((r) => (
              <RoutineCard
                key={r.id}
                routine={r}
                onDelete={() => handleDelete(r.id)}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
